import React, { useRef, useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { useImportAnki } from "@/hooks/useImportAnki";
import { Loader2, Upload, Sparkles, Eye, X, AlertTriangle, Trash2, FileText, CalendarIcon } from "lucide-react";
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { parseAnkiQuestion } from "@/utils/anki-utils";
import { formatChatMessage } from "@/utils/formatUtils";
import { deleteAllQuestionsWithSource, deleteAllQuestions, countQuestionsBySource } from "@/utils/questionStats";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

interface AnkiImportProps {
  subject: string;
  onImport: () => void;
}

interface PreviewQuestion {
  question: string;
  options: string[];
  answer: string;
  isValid: boolean;
  difficulty?: string;
  reason?: string;
}

const AnkiImport: React.FC<AnkiImportProps> = ({ subject, onImport }) => {
  const { importAnki, isImporting, importStats, clearImportStats } = useImportAnki();
  const [open, setOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [detailedPreviewOpen, setDetailedPreviewOpen] = useState(false);
  const [statsOpen, setStatsOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteAllDialogOpen, setDeleteAllDialogOpen] = useState(false);
  const [isDeletingAll, setIsDeletingAll] = useState(false);
  const [previewQuestions, setPreviewQuestions] = useState<PreviewQuestion[]>([]);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [allQuestions, setAllQuestions] = useState<PreviewQuestion[]>([]);
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
  const [showDateSelector, setShowDateSelector] = useState(false);
  const [timePeriod, setTimePeriod] = useState<string>("daily");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [importButtonDisabled, setImportButtonDisabled] = useState(true);

  const extractSubjectAndSection = () => {
    const regex = /^(.+?)(?: - (.+?))?$/;
    const matches = subject.match(regex);
    
    if (matches && matches.length >= 3 && matches[2]) {
      return matches[2];
    }
    
    return subject;
  };

  const actualSubject = extractSubjectAndSection();

  useEffect(() => {
    setShowDateSelector(actualSubject === 'Current Affairs');
    
    setImportButtonDisabled(!selectedFile || allQuestions.length === 0 || 
      (actualSubject === 'Current Affairs' && !selectedDate));
  }, [selectedFile, allQuestions, actualSubject, selectedDate]);

  const handleFileSelected = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    setSelectedFile(file);
    
    try {
      const text = await file.text();
      const lines = text
        .split('\n')
        .map(line => line.trim())
        .filter(line => line.length > 0 && line.includes('\t'));
      
      const parsedQuestions: PreviewQuestion[] = [];
      const allParsedQuestions: PreviewQuestion[] = [];
      let validCount = 0;
      let invalidCount = 0;
      
      for (let i = 0; i < lines.length; i++) {
        const result = parseAnkiQuestion(lines[i], actualSubject);
        const question: PreviewQuestion = result 
          ? {
              question: result.data.Question,
              options: [
                result.data["Option A"],
                result.data["Option B"],
                result.data["Option C"],
                result.data["Option D"]
              ],
              answer: result.data["Correct Answer"],
              difficulty: result.data.Difficulty,
              isValid: true
            }
          : (() => {
              const parts = lines[i].split('\t');
              if (parts.length >= 2) {
                return {
                  question: parts[0].replace(/<[^>]*>/g, '').split('Options:')[0] || 'Invalid format',
                  options: ['Invalid option format'],
                  answer: 'Unknown',
                  isValid: false,
                  reason: 'Could not parse question format correctly'
                };
              }
              return null;
            })();
        
        if (question) {
          allParsedQuestions.push(question);
          
          if (i < 25) {
            parsedQuestions.push(question);
          }
          
          if (question.isValid) {
            validCount++;
          } else {
            invalidCount++;
          }
        }
      }
      
      setPreviewQuestions(parsedQuestions);
      setAllQuestions(allParsedQuestions);
      
      if (validCount === 0 && invalidCount > 0) {
        toast.error('No valid questions found in the file', {
          description: 'Check that your Anki export is in the correct format'
        });
      } else if (invalidCount > 0) {
        toast.warning(`${invalidCount} questions may have issues`, {
          description: `${validCount} questions are valid and will be imported`
        });
      }
    } catch (error) {
      console.error("Error previewing Anki file:", error);
      toast.error("Failed to preview Anki deck", {
        description: "Error parsing the file format",
      });
    }
  };

  function handleImport() {
    if (!selectedFile) {
      toast.error("No file selected");
      return;
    }
    if (actualSubject === 'Current Affairs' && !selectedDate) {
      toast.error("Please select a date for Current Affairs questions");
      return;
    }

    importAnki(
      actualSubject,
      selectedFile,
      actualSubject === 'Current Affairs' ? selectedDate : undefined,
      actualSubject === 'Current Affairs' ? timePeriod : undefined
    ).then(success => {
      if (success) {
        setStatsOpen(true);
        setOpen(false);
        setPreviewOpen(false);
        setDetailedPreviewOpen(false);
        resetState();
        onImport();
      }
    }).catch(error => {
      console.error("Error importing Anki file:", error);
      toast.error("Failed to import Anki deck", {
        description: "Please try again or check if the file is in the correct format",
      });
    });
  }

  const resetState = () => {
    setSelectedFile(null);
    setPreviewQuestions([]);
    setAllQuestions([]);
    setSelectedDate(undefined);
    setTimePeriod("daily");
    
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleDeleteAllQuestions = async () => {
    setIsDeletingAll(true);
    try {
      const result = await deleteAllQuestions();
      
      if (result.error) {
        toast.error("Failed to delete all questions", {
          description: result.error
        });
      } else {
        toast.success(`Deleted all questions (${result.count})`, {
          description: "You can now import fresh questions for each category"
        });

        resetState();
        clearImportStats();
        
        onImport();
      }
    } catch (error) {
      console.error("Error deleting all questions:", error);
      toast.error("Failed to delete all questions", {
        description: "An unexpected error occurred"
      });
    } finally {
      setIsDeletingAll(false);
      setDeleteAllDialogOpen(false);
    }
  };

  const handleOpenPreview = () => {
    if (previewQuestions.length > 0) {
      setPreviewOpen(true);
    } else {
      toast.error("No preview available", {
        description: "Please select a valid Anki file first",
      });
    }
  };
  
  const handleOpenDetailedPreview = () => {
    if (allQuestions.length > 0) {
      setDetailedPreviewOpen(true);
    } else {
      toast.error("No questions available", {
        description: "Please select a valid Anki file first",
      });
    }
  };
  
  const handleDeleteQuestion = (index: number) => {
    const newQuestions = [...allQuestions];
    newQuestions.splice(index, 1);
    setAllQuestions(newQuestions);
    
    if (index < previewQuestions.length) {
      const newPreviewQuestions = [...previewQuestions];
      newPreviewQuestions.splice(index, 1);
      setPreviewQuestions(newPreviewQuestions);
    }
    
    toast.success("Question removed from import", {
      description: `${newQuestions.length} questions remaining`
    });
  };

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={() => setOpen(true)}
        disabled={isImporting}
        className="w-full bg-gradient-to-r from-[#0CDBDF]/90 to-[#0CDBDF]/70 text-white border-transparent hover:bg-[#09afb2] hover:text-white transition-all duration-300 relative overflow-hidden group shadow-sm"
      >
        <span className="absolute inset-0 w-full h-full bg-gradient-to-r from-[#0CDBDF]/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></span>
        
        {isImporting ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Importing...
          </>
        ) : (
          <>
            <div className="flex items-center justify-center gap-2">
              <Upload className="h-4 w-4" />
              <span>Import Anki</span>
              <Sparkles className="h-3 w-3 text-yellow-200 animate-pulse-subtle" />
            </div>
          </>
        )}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md bg-white dark:bg-gray-900 border dark:border-gray-800 shadow-xl">
          <DialogHeader>
            <DialogTitle>Import Anki Deck</DialogTitle>
            <DialogDescription>
              Select an Anki export file (.txt) to import questions for {actualSubject}
              {subject !== actualSubject && (
                <span className="block text-xs mt-1">
                  Importing for section: <strong>{actualSubject}</strong>
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="flex flex-col items-center gap-4">
              <input
                ref={fileInputRef}
                type="file"
                accept=".txt"
                onChange={handleFileSelected}
                className="block w-full text-sm text-gray-500
                  file:mr-4 file:py-2 file:px-4
                  file:rounded-md file:border-0
                  file:text-sm file:font-semibold
                  file:bg-primary file:text-white
                  hover:file:bg-primary/90"
              />
              <p className="text-xs text-gray-500">
                Only .txt files exported from Anki are supported
              </p>
              
              {showDateSelector && (
                <div className="w-full space-y-2">
                  <label className="text-sm font-medium">Select Date for Current Affairs Questions:</label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal",
                          !selectedDate && "text-muted-foreground"
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {selectedDate ? format(selectedDate, "PPP") : <span>Select date</span>}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={selectedDate}
                        onSelect={setSelectedDate}
                        initialFocus
                        className={cn("p-3 pointer-events-auto")}
                      />
                    </PopoverContent>
                  </Popover>
                  
                  <div className="mt-4">
                    <label className="text-sm font-medium">Time Period:</label>
                    <Select value={timePeriod} onValueChange={setTimePeriod}>
                      <SelectTrigger className="w-full mt-1">
                        <SelectValue placeholder="Select time period" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="daily">Daily</SelectItem>
                        <SelectItem value="weekly">Weekly</SelectItem>
                        <SelectItem value="monthly">Monthly</SelectItem>
                        <SelectItem value="yearly">Yearly</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {actualSubject === 'Current Affairs' && !selectedDate && (
                    <p className="text-xs text-amber-500">
                      A date is required when importing Current Affairs questions
                    </p>
                  )}
                </div>
              )}
              
              <div className="flex gap-2 w-full">
                <Button 
                  variant="outline"
                  onClick={handleOpenPreview}
                  disabled={previewQuestions.length === 0}
                  className="flex-1"
                >
                  <Eye className="mr-2 h-4 w-4" />
                  Quick Preview
                </Button>
                
                <Button 
                  variant="outline"
                  onClick={handleOpenDetailedPreview}
                  disabled={allQuestions.length === 0}
                  className="flex-1"
                >
                  <FileText className="mr-2 h-4 w-4" />
                  Detailed Review
                </Button>
              </div>
              
              <Button 
                variant="default" 
                onClick={handleImport}
                disabled={importButtonDisabled}
                className="w-full"
              >
                Import Now
              </Button>
              
              <div className="flex flex-col gap-3 mt-4">
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => setDeleteDialogOpen(true)}
                  disabled={isDeleting}
                  className="flex items-center"
                >
                  {isDeleting ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Trash2 className="mr-2 h-4 w-4" />
                  )}
                  Delete All Anki Questions
                </Button>
                
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => setDeleteAllDialogOpen(true)}
                  disabled={isDeletingAll}
                  className="flex items-center"
                >
                  {isDeletingAll ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Trash2 className="mr-2 h-4 w-4" />
                  )}
                  Delete ALL Questions (All Sources)
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-white dark:bg-gray-900">
          <AlertDialogHeader>
            <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete all previously imported Anki questions.
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteAllQuestions} disabled={isDeleting}>
              {isDeleting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : "Yes, Delete All"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={deleteAllDialogOpen} onOpenChange={setDeleteAllDialogOpen}>
        <AlertDialogContent className="bg-white dark:bg-gray-900">
          <AlertDialogHeader>
            <AlertDialogTitle>WARNING: Delete ALL Questions</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete ALL questions in the database regardless of their source.
              This action cannot be undone. Are you absolutely certain?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteAllQuestions} disabled={isDeletingAll}>
              {isDeletingAll ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : "Yes, Delete ALL Questions"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Sheet open={previewOpen} onOpenChange={setPreviewOpen}>
        <SheetContent className="sm:max-w-lg w-[90%] overflow-y-auto bg-white dark:bg-gray-900 border dark:border-gray-800">
          <SheetHeader>
            <SheetTitle className="flex items-center justify-between">
              <span>Preview Questions</span>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => setPreviewOpen(false)}
                className="h-8 w-8 p-0 rounded-full"
              >
                <X className="h-4 w-4" />
              </Button>
            </SheetTitle>
          </SheetHeader>
          
          <div className="mt-6 space-y-6">
            {previewQuestions.length === 0 ? (
              <p className="text-center text-gray-500">No questions to preview</p>
            ) : (
              <>
                <p className="text-sm text-gray-500">
                  Showing {previewQuestions.length} questions from file 
                  {previewQuestions.length < 25 ? '.' : ' (max 25 shown).'}
                </p>
                
                {previewQuestions.map((question, index) => (
                  <div 
                    key={index} 
                    className={`border rounded-lg p-4 space-y-3 ${
                      !question.isValid ? 'border-amber-300 bg-amber-50' : ''
                    }`}
                  >
                    <div className="flex justify-between items-start">
                      <h3 className="font-medium text-base">Question {index + 1}</h3>
                      {!question.isValid && (
                        <div className="flex items-center text-amber-600 text-xs">
                          <AlertTriangle className="h-3 w-3 mr-1" />
                          Invalid Format
                        </div>
                      )}
                    </div>
                    
                    <p className="text-gray-800">
                      {question.question}
                      {!question.isValid && (
                        <span className="text-amber-600 text-xs block mt-1">
                          {question.reason || 'This question has format issues and will be skipped'}
                        </span>
                      )}
                    </p>
                    
                    {question.isValid && (
                      <>
                        <div className="space-y-2 mt-2">
                          <p className="font-medium text-sm">Options:</p>
                          <ul className="space-y-1 pl-5 list-disc">
                            {question.options.map((option, optIndex) => (
                              <li 
                                key={optIndex} 
                                className={`text-sm ${option === question.answer ? 'font-semibold text-green-600' : ''}`}
                              >
                                {option}
                                {option === question.answer && ' (Correct)'}
                              </li>
                            ))}
                          </ul>
                        </div>
                        
                        {question.difficulty && (
                          <div className="mt-2 text-sm">
                            <span className="font-medium">Difficulty:</span> 
                            <span className={`ml-1 ${
                              question.difficulty === 'easy' ? 'text-green-600' : 
                              question.difficulty === 'medium' ? 'text-amber-600' : 
                              'text-red-600'
                            }`}>
                              {question.difficulty.charAt(0).toUpperCase() + question.difficulty.slice(1)}
                            </span>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                ))}
                
                <div className="flex justify-end pt-4">
                  <Button variant="default" onClick={handleImport}>
                    Import Questions
                  </Button>
                </div>
              </>
            )}
          </div>
        </SheetContent>
      </Sheet>
      
      <Sheet open={detailedPreviewOpen} onOpenChange={setDetailedPreviewOpen}>
        <SheetContent side="right" className="w-full md:max-w-xl overflow-y-auto bg-white dark:bg-gray-900 border dark:border-gray-800">
          <SheetHeader>
            <SheetTitle className="flex items-center justify-between">
              <span>Question Review</span>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => setDetailedPreviewOpen(false)}
                className="h-8 w-8 p-0 rounded-full"
              >
                <X className="h-4 w-4" />
              </Button>
            </SheetTitle>
          </SheetHeader>
          
          <div className="mt-6 space-y-4">
            <div className="flex justify-between items-center">
              <p className="text-sm">
                <span className="font-medium">Total questions:</span> {allQuestions.length}
              </p>
              <Button 
                variant="outline"
                size="sm"
                onClick={handleImport}
                disabled={allQuestions.length === 0}
              >
                Import All
              </Button>
            </div>
            
            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-8">#</TableHead>
                    <TableHead>Question</TableHead>
                    <TableHead className="w-24">Difficulty</TableHead>
                    <TableHead className="w-16 text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {allQuestions.map((question, index) => (
                    <TableRow 
                      key={index}
                      className={!question.isValid ? 'bg-amber-50' : ''}
                    >
                      <TableCell>{index + 1}</TableCell>
                      <TableCell className="max-w-[240px] truncate">
                        <span 
                          className="cursor-help" 
                          title={question.question}
                        >
                          {question.question.length > 50 
                            ? `${question.question.substring(0, 50)}...` 
                            : question.question}
                        </span>
                      </TableCell>
                      <TableCell>
                        {!question.isValid ? (
                          <span className="text-amber-600">Invalid</span>
                        ) : (
                          <span className={
                            question.difficulty === 'easy' ? 'text-green-600' : 
                            question.difficulty === 'medium' ? 'text-amber-600' : 
                            'text-red-600'
                          }>
                            {question.difficulty?.charAt(0).toUpperCase() + question.difficulty?.slice(1)}
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0"
                          onClick={() => handleDeleteQuestion(index)}
                        >
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            
            <div className="flex justify-end pt-4">
              <Button 
                variant="default" 
                onClick={handleImport}
                disabled={allQuestions.length === 0}
              >
                Import Selected ({allQuestions.length})
              </Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      <Dialog open={statsOpen} onOpenChange={setStatsOpen}>
        <DialogContent className="sm:max-w-md bg-white dark:bg-gray-900 border dark:border-gray-800">
          <DialogHeader>
            <DialogTitle>Import Results</DialogTitle>
          </DialogHeader>
          
          {importStats && (
            <div className="space-y-4 py-4">
              <div className="flex justify-around text-center">
                <div>
                  <p className="text-2xl font-bold text-green-600">{importStats.imported}</p>
                  <p className="text-sm text-gray-500">Imported</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-amber-600">{importStats.rejected}</p>
                  <p className="text-sm text-gray-500">Rejected</p>
                </div>
                <div>
                  <p className="text-2xl font-bold">{importStats.total}</p>
                  <p className="text-sm text-gray-500">Total</p>
                </div>
              </div>
              
              {importStats.rejected > 0 && (
                <div className="border rounded-lg p-3 bg-gray-50">
                  <p className="font-medium text-sm mb-2">Reasons for rejection:</p>
                  <ul className="space-y-1 text-sm">
                    {Object.entries(importStats.rejectionReasons).map(([reason, count]) => (
                      <li key={reason} className="flex justify-between">
                        <span>{reason}</span>
                        <span className="font-medium">{count}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              
              <Button 
                className="w-full" 
                onClick={() => setStatsOpen(false)}
              >
                Close
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AnkiImport;
